/**
 * VersionDiffView — a unified line diff of two version texts.
 *
 * Two-number gutter (old / new line numbers), a +/− marker column (colour is
 * never the only signal — the marker and a screen-reader label carry it too),
 * word-level emphasis on paired changed lines, and long unchanged runs folded
 * into "Show N unchanged lines" rows that expand in place. The header strip
 * counts the change ("+N added · −M removed") and toggles Changes only / Full
 * text.
 *
 * `flush` drops the card frame and makes the header sticky, for use edge to
 * edge inside a scrolling pane (the version-history modal).
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { GitCompareArrows, UnfoldVertical } from 'lucide-react';
import SegmentedControl from '../../../ui/SegmentedControl';
import { collapseUnchanged, diffLines, diffStats } from './textDiff';
import type { DiffGap, DiffRow, DisplayRow } from './textDiff';

export type DiffDisplayMode = 'changes' | 'full';

export interface VersionDiffViewProps {
  oldText: string;
  newText: string;
  /** Unchanged lines kept either side of each change in "Changes only" (default 3). */
  context?: number;
  /** Initial display mode (default 'changes'). */
  defaultMode?: DiffDisplayMode;
  /** Shown instead of the diff when the two texts are identical. */
  emptyMessage?: ReactNode;
  /** No card frame + sticky header, for an edge-to-edge scrolling pane. */
  flush?: boolean;
  className?: string;
}

const MODE_OPTIONS = [
  { value: 'changes', label: 'Changes only' },
  { value: 'full', label: 'Full text' },
];

const EMPTY_SET: ReadonlySet<number> = new Set<number>();

/** Row + gutter tints; written out in full so Tailwind can see every class. */
const ROW_CLS: Record<DiffRow['type'], string> = {
  same: 'text-gray-700 dark:text-gray-300',
  add: 'bg-green-50 text-green-900 dark:bg-green-900/20 dark:text-green-200',
  del: 'bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-200',
};
const NUM_CLS: Record<DiffRow['type'], string> = {
  same: 'text-gray-400 dark:text-gray-500',
  add: 'bg-green-100/60 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  del: 'bg-red-100/60 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};
const EMPHASIS_CLS: Record<'add' | 'del', string> = {
  add: 'rounded-sm bg-green-200/70 box-decoration-clone dark:bg-green-800/60',
  del: 'rounded-sm bg-red-200/70 box-decoration-clone dark:bg-red-800/60',
};

function LineContent({ row }: { row: DiffRow }) {
  if (row.segments && row.type !== 'same') {
    const emph = EMPHASIS_CLS[row.type];
    return (
      <>
        {row.segments.map((s, i) => (s.changed
          ? <span key={i} className={emph}>{s.text}</span>
          : <span key={i}>{s.text}</span>))}
      </>
    );
  }
  // A lone space keeps an empty line one line tall under pre-wrap.
  return <>{row.text === '' ? ' ' : row.text}</>;
}

function DiffLine({ row }: { row: DiffRow }) {
  const num = `select-none px-2 text-right align-top tabular-nums ${NUM_CLS[row.type]}`;
  return (
    <tr data-diff={row.type} className={ROW_CLS[row.type]}>
      <td className={num}>{row.oldNo ?? ''}</td>
      <td className={num}>{row.newNo ?? ''}</td>
      <td className={`select-none text-center align-top ${NUM_CLS[row.type]}`}>
        {row.type === 'add' && (<><span aria-hidden="true">+</span><span className="sr-only">Added</span></>)}
        {row.type === 'del' && (<><span aria-hidden="true">−</span><span className="sr-only">Removed</span></>)}
      </td>
      <td className="whitespace-pre-wrap break-words pl-1 pr-3 align-top">
        <LineContent row={row} />
      </td>
    </tr>
  );
}

export default function VersionDiffView({
  oldText,
  newText,
  context = 3,
  defaultMode = 'changes',
  emptyMessage = 'No differences.',
  flush = false,
  className = '',
}: VersionDiffViewProps) {
  const rows = useMemo(() => diffLines(oldText, newText), [oldText, newText]);
  const stats = useMemo(() => diffStats(rows), [rows]);
  const [mode, setMode] = useState<DiffDisplayMode>(defaultMode);
  // Expanded gaps belong to one diff; a new pair of texts starts folded again.
  const [expanded, setExpanded] = useState<{ rows: DiffRow[]; starts: ReadonlySet<number> }>({ rows, starts: EMPTY_SET });
  const expandedStarts = expanded.rows === rows ? expanded.starts : EMPTY_SET;

  const display: DisplayRow[] = useMemo(
    () => (mode === 'full' ? rows : collapseUnchanged(rows, context)),
    [mode, rows, context],
  );

  const expand = (gap: DiffGap) => {
    const next = new Set(expandedStarts);
    next.add(gap.start);
    setExpanded({ rows, starts: next });
  };

  const identical = stats.added === 0 && stats.removed === 0;
  const frame = flush
    ? ''
    : 'overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800';

  if (identical) {
    return (
      <div className={`${frame} ${className}`}>
        <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
          <GitCompareArrows className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" aria-hidden="true" />
          <p className="max-w-sm text-sm text-gray-600 dark:text-gray-400">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Line-number columns sized to the widest number either side.
  let maxNo = 1;
  for (const r of rows) maxNo = Math.max(maxNo, r.oldNo ?? 0, r.newNo ?? 0);
  const numWidth = `calc(${String(maxNo).length}ch + 1rem)`;

  const body: ReactNode[] = [];
  display.forEach((r, idx) => {
    if (r.type !== 'gap') {
      body.push(<DiffLine key={`r${idx}`} row={r} />);
      return;
    }
    if (expandedStarts.has(r.start)) {
      for (let t = r.start; t < r.start + r.count; t += 1) body.push(<DiffLine key={`g${r.start}-${t}`} row={rows[t]} />);
      return;
    }
    body.push(
      <tr key={`gap${r.start}`}>
        <td colSpan={4} className="p-0">
          <button
            type="button"
            onClick={() => expand(r)}
            className="flex min-h-[44px] w-full items-center justify-center gap-1.5 border-y border-gray-200 bg-gray-50 px-3 font-sans text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0 sm:py-1"
          >
            <UnfoldVertical className="h-3.5 w-3.5" aria-hidden="true" />
            Show {r.count.toLocaleString()} unchanged line{r.count === 1 ? '' : 's'}
          </button>
        </td>
      </tr>,
    );
  });

  return (
    <div className={`${frame} ${className}`}>
      <div
        className={`flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800 ${flush ? 'sticky top-0 z-10' : ''}`}
      >
        <p className="text-xs font-medium tabular-nums text-gray-600 dark:text-gray-400" aria-live="polite">
          <span className="text-green-700 dark:text-green-400">+{stats.added.toLocaleString()} added</span>
          <span className="mx-1.5 text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
          <span className="text-red-700 dark:text-red-400">−{stats.removed.toLocaleString()} removed</span>
        </p>
        <SegmentedControl
          label="Diff display"
          options={MODE_OPTIONS}
          value={mode}
          onChange={(v) => setMode(v as DiffDisplayMode)}
        />
      </div>
      <table aria-label="Differences" className="w-full table-fixed border-collapse font-mono text-xs leading-5">
        <colgroup>
          <col style={{ width: numWidth }} />
          <col style={{ width: numWidth }} />
          <col className="w-5" />
          <col />
        </colgroup>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}
